import { static_errors } from './constants.ts'
import { z, ZodError, ZodTypeAny } from 'zod'
import axios, { AxiosError } from 'axios'
import { instanceOfError, RequestError } from './types_and_interfaces.ts'
import { twMerge } from 'tailwind-merge'
import clsx from 'clsx'
import { ClassValue } from 'class-variance-authority/types'
import { ColorType } from '@/common/components/Badge.tsx'
import * as Sentry from '@sentry/react'

export const parseRequestError = (
  error: unknown | AxiosError | ZodError
): RequestError => {
  if (error instanceof ZodError) {
    Sentry.captureException(error)

    return {
      code: -2,
      message: static_errors.vital_parsing_error,
      details: null,
    }
  }

  if (axios.isAxiosError(error)) {
    const response = error.response
    if (response && instanceOfError(response.data)) {
      return response.data.error
    } else if (response?.data?.error || response?.data?.message) {
      return {
        code: response.status,
        message: response?.data?.error ?? response?.data?.message ?? '',
        details: null,
      }
    }

    return {
      code: -1,
      message: static_errors.unexpected_contact_support,
      details: null,
    }
  } else {
    return {
      code: -1,
      message: static_errors.unexpected_contact_support,
      details: null,
    }
  }
}

export const parseBlobRequestError = async (
  error: unknown | AxiosError | ZodError
): Promise<RequestError> => {
  if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
    try {
      const blobText = await error.response.data.text()
      const parsedData = JSON.parse(blobText)

      if (instanceOfError(parsedData)) {
        return parsedData.error
      }

      const message =
        typeof parsedData?.message === 'string'
          ? parsedData.message
          : typeof parsedData?.error === 'string'
            ? parsedData.error
            : ''

      if (message) {
        return {
          code: error.response.status ?? -1,
          message,
          details: null,
        }
      }
    } catch {
      // fall back to the shared parser if the blob is not valid JSON.
    }
  }

  return parseRequestError(error)
}

export const optionsDate = {
  month: '2-digit' as const,
  day: '2-digit' as const,
  year: 'numeric' as const,
  hour: '2-digit' as const,
  minute: '2-digit' as const,
  hour12: true,
  timeZone: 'PST',
} as const

export const cn = (...inputs: ClassValue[]) => {
  return twMerge(clsx(inputs))
}

export const getSemanticColor = (
  status: 'Active' | 'Pending' | 'Inactive'
): ColorType => {
  if (status == 'Active') return 'Positive'
  if (status == 'Pending') return 'Notice'
  return 'Negative'
}

export const getAppealsSemanticColor = (
  status: 'New' | 'Review' | 'Accepted' | 'Rejected'
): ColorType => {
  if (status == 'New') return 'Informative'
  if (status == 'Review') return 'Notice'
  if (status == 'Accepted') return 'Positive'
  if (status == 'Rejected') return 'Negative'
  return 'Negative'
}

type KeySubset<T, K extends keyof T> = {
  [P in K]: T[P]
}

export function pick<T extends object, K extends keyof T>(
  obj: T,
  keys: K[]
): KeySubset<T, K> {
  const result = {} as KeySubset<T, K>
  keys.forEach((key) => {
    if (key in obj) {
      result[key] = obj[key]
    }
  })
  return result
}

export function createUUID() {
  if (window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID()
  } else {
    // Fallback for older browsers
    return generateUUIDv4()
  }
}

function generateUUIDv4() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export function mockAPI<ResponseType>(
  returnData: ResponseType,
  milliseconds: number = 1500
) {
  return new Promise<ResponseType>((resolve) =>
    setTimeout(() => resolve(returnData), milliseconds)
  )
}

export const getPageData = <T>(
  list: Array<T>,
  itemsPerPage: number,
  currentPage: number
): T[] => {
  const startIndex = (currentPage - 1) * itemsPerPage
  return list.slice(startIndex, startIndex + itemsPerPage)
}

export const getPaginatedSchema = (dataSchema: ZodTypeAny) => {
  return z.object({
    data: z.array(dataSchema).nullable(),
    links: z.object({
      first: z.string(),
      last: z.string(),
      prev: z.null(),
      next: z.null(),
    }),
    meta: z.object({
      current_page: z.number(),
      from: z.number(),
      last_page: z.number(),
      links: z.array(
        z.union([
          z.object({ url: z.null(), label: z.string(), active: z.boolean() }),
          z.object({ url: z.string(), label: z.string(), active: z.boolean() }),
        ])
      ),
      path: z.string(),
      per_page: z.number(),
      to: z.number(),
      total: z.number(),
    }),
  })
}

export const currencyFormatter = new Intl.NumberFormat('en-US', {
  currency: 'USD',
  style: 'currency',
})

/**
 * Formats a given phone number string into a standard US phone number format.
 *
 * @param phoneNumberString - The phone number string to be formatted. It may contain non-numeric characters.
 * @returns A formatted phone number string in the format (XXX) XXX-XXXX if the input is valid, otherwise null.
 */
export function formatPhoneNumber(phoneNumberString: string) {
  const cleaned = ('' + phoneNumberString).replace(/\D/g, '')

  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
  if (match) {
    return '(' + match[1] + ') ' + match[2] + '-' + match[3]
  }
  return null
}

export const sleep = (seconds: number) => {
  return new Promise((resolve) => {
    setTimeout(resolve, seconds * 1000)
  })
}

export const convertFileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = (error) => reject(error)
  })
}
